import React, { useState, useEffect } from 'react';
// The docs suggest that type of import below is preferred for performance.
import { Document, Page, pdfjs } from 'react-pdf/dist/esm/entry.webpack';
import { updatePdfPage } from '@services/sse';
import { usePresentation } from '@context/presentation';
import { setPdfPage } from '@actions/creators';
import { generateStorageBlobUrl } from '@utils/generateAssetLinks';
import { BLOB_TYPES, BROWSER_STORAGE_KEYS, SCROLL_IDS } from '@constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStepForward, faStepBackward } from '@fortawesome/free-solid-svg-icons';
import { PdfDisplayStyle, DocumentStyle, PdfButtonsContainer, PdfButton } from './styles';
import { retrieveSavedData, setBrowserStorageItem } from '@utils/browserStorage';
import { handleScroll, updateRefScrollPosition, getScrollId } from '@utils/handleScroll';
import { isValidNumber } from '@utils/validateInputs';
import { retrieveScrollPosition } from '@utils/browserStorage';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

// Some props are optional depending on who is viewing.
export const SinglePagePdf = ({ slideRef, slideState, dispatch, children }) => {
    const { client, clientViewing } = usePresentation();
    const { firmShortName } = client;
    const { pdfPageKey } = BROWSER_STORAGE_KEYS;
    const { pdfBody } = SCROLL_IDS;
    const { pdfPageNumber, scrollTop } = slideState;
    const scrollId = getScrollId(pdfBody, pdfPageNumber);
    const currentScrollPosition = scrollTop ? scrollTop[scrollId] : null;
    const pdfUrl = generateStorageBlobUrl(BLOB_TYPES.pdf, firmShortName);
    
    const [numPages, setNumPages] = useState(null);
    const [hoverActive, setHoverActive] = useState(false);
    const [error, setError] = useState({
        status: false,
        message: ''
    });

    useEffect(() => {
        retrieveSavedData([pdfPageKey], dispatch);
    }, []);

    useEffect(() => {
        retrieveScrollPosition(scrollId, slideRef);
    }, [slideRef, pdfPageNumber]);

    useEffect(() => {
        if (isValidNumber(currentScrollPosition) && clientViewing) {
            updateRefScrollPosition(slideRef, currentScrollPosition);
        }
    }, [slideRef, currentScrollPosition]);
  
    function onDocumentLoadSuccess({ numPages }) {
        setNumPages(numPages);
    }

    function onDocumentLoadError(err) {
        console.error(err);
        setError({ status: true, message: err.message });
    }

    function changePage(offset) {
        const newPage = pdfPageNumber + offset;

        dispatch(setPdfPage(newPage));
        updatePdfPage(newPage);
        setBrowserStorageItem(pdfPageKey, newPage);
    }

    // This function will only be available for the rep.
    function previousPage() {
        changePage(-1);
    }

    function nextPage() {
        changePage(1);
    }

    if (error.status) return (
        <div>Error loading pdf: {error.message}</div>
    );

    return (
        <PdfDisplayStyle>
            <DocumentStyle
                data-testid='single-page-pdf'
                clientViewing={clientViewing}
                id={scrollId}
                onScroll={event => handleScroll(event, scrollId, clientViewing)}
                onMouseEnter={() => setHoverActive(true)}
                onMouseLeave={() => setHoverActive(false)}
                ref={slideRef}>
                <Document
                    file={pdfUrl}
                    onLoadError={onDocumentLoadError}
                    onLoadSuccess={onDocumentLoadSuccess}>
                        <Page pageNumber={pdfPageNumber}>
                            {children}
                        </Page>
                </Document>
            </DocumentStyle>
            {
                !clientViewing && (
                    <PdfButtonsContainer  
                        data-testid='hover-buttons'
                        onMouseEnter={() => setHoverActive(true)}
                        onMouseLeave={() => setHoverActive(false)}
                        hoverActive={hoverActive}>
                        <PdfButton
                            disabled={pdfPageNumber <= 1}
                            onClick={previousPage}>
                                <FontAwesomeIcon icon={faStepBackward} />
                                Previous Page
                        </PdfButton>
                        <span>Page {pdfPageNumber} of {numPages}</span>
                        <PdfButton
                            disabled={pdfPageNumber >= numPages}
                            onClick={nextPage}>
                                Next Page
                                <FontAwesomeIcon icon={faStepForward} />
                        </PdfButton>
                    </PdfButtonsContainer>
                )
            }
        </PdfDisplayStyle>
    );
};

export default SinglePagePdf;